import { useNavigate } from 'react-router-dom';
import { FaFacebookF, FaInstagram, FaLinkedin } from 'react-icons/fa';
import { FaYoutube } from 'react-icons/fa6';
import { FaXTwitter } from 'react-icons/fa6';
import '../styles/LandingPageNew.css';
import { galleryAPI, bannerAPI } from '../utils/api';
import NoticeBar from '../components/NoticeBar';
import { useEffect, useState, useRef, useCallback } from 'react';

const STATIC_BANNERS = [
  { _id: 's1', url: '/images/banner1.png', title: 'Banner 1' },
  { _id: 's2', url: '/images/banner2.png', title: 'Banner 2' },
  { _id: 's3', url: '/images/banner3.png', title: 'Banner 3' },
  { _id: 's4', url: '/images/banner4.png', title: 'Banner 4' },
  { _id: 's5', url: '/images/banner5.png', title: 'Banner 5' },
  { _id: 's6', url: '/images/banner6.png', title: 'Banner 6' },
];

const LandingPage = () => {
  const navigate = useNavigate();
  const [galleryImages, setGalleryImages] = useState([]);
  const [bannerList, setBannerList] = useState(STATIC_BANNERS);
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
  const bannerIndexRef = useRef(0);
  const bannerAutoRef = useRef(null);
  const [currentCarouselIndex, setCurrentCarouselIndex] = useState(0);
  const carouselRef = useRef(null);
  const carouselIndexRef = useRef(0);
  const autoScrollRef = useRef(null);

  useEffect(() => {
    galleryAPI.getAll()
      .then(data => {
        const imgs = data.filter(i => i.type === 'image');
        setGalleryImages(imgs);
      })
      .catch(() => {});

    bannerAPI.getAll()
      .then(data => {
        if (Array.isArray(data) && data.length > 0) setBannerList(data);
      })
      .catch(() => {});
  }, []);

  const goToBanner = useCallback((index) => {
    bannerIndexRef.current = index;
    setCurrentBannerIndex(index);
  }, []);

  const nextBanner = useCallback(() => {
    const next = (bannerIndexRef.current + 1) % bannerList.length;
    goToBanner(next);
  }, [bannerList.length, goToBanner]);

  useEffect(() => {
    if (bannerList.length < 2) return;
    bannerAutoRef.current = setInterval(nextBanner, 3500);
    return () => clearInterval(bannerAutoRef.current);
  }, [bannerList, nextBanner]);

  const scrollCarousel = useCallback((direction) => {
    const el = carouselRef.current;
    if (!el) return;
    const cardWidth = el.offsetWidth / 2;
    const maxIndex = Math.max(0, galleryImages.length - 2);
    let next = carouselIndexRef.current + direction;
    if (next > maxIndex) next = 0;
    if (next < 0) next = maxIndex;
    carouselIndexRef.current = next;
    setCurrentCarouselIndex(next);
    el.scrollTo({ left: next * cardWidth, behavior: 'smooth' });
  }, [galleryImages.length]);

  useEffect(() => {
    if (galleryImages.length < 2) return;
    autoScrollRef.current = setInterval(() => scrollCarousel(1), 3500);
    return () => clearInterval(autoScrollRef.current);
  }, [galleryImages, scrollCarousel]);

  const testimonials = [
    {
      name: 'Rahul Kumar',
      exam: 'CBSE Class XII - 98.4%',
      text: 'Vidya Niketan provided me with comprehensive study material and live classes that helped me secure 98.4% in CBSE Class XII 2025. The teachers are exceptional!',
      achievement: 'AISSCE'
    },
    {
      name: 'Priya Sharma',
      exam: 'CBSE Class X - 97.2%',
      text: 'The online platform is very user-friendly. I could study at my own pace and still get live doubt-clearing sessions whenever needed.',
      achievement: 'AISSE'
    },
    {
      name: 'Aditya Singh',
      exam: 'JEE Advanced - 2025',
      text: 'Quality content combined with affordable pricing. Vidya Niketan made premium education accessible to everyone.',
      achievement: 'JEE Advanced'
    }
  ];

  const features = [
    { icon: '🎥', title: 'Live Interactive Classes', desc: 'Daily live sessions with experienced teachers' },
    { icon: '📚', title: 'Study Materials', desc: 'Tests, sample papers & notes' },
    { icon: '❓', title: '24x7 Doubt Sessions', desc: 'Clear your doubts anytime, anywhere' },
    { icon: '🎯', title: 'Progress Tracking', desc: 'Smart dashboard to monitor your growth' },
    { icon: '💻', title: 'Offline Centers', desc: '100+ offline centers across India' },
    { icon: '🏆', title: 'Proven Results', desc: 'Thousands of successful students' }
  ];

  const exams = [
    'IIT JEE', 'NEET', 'SSC', 'Banking', 'Defence', 'Board Exams'
  ];

  return (
    <div className="landing-page">
      {/* Banner Carousel Section */}
      <section className="banner-carousel-section">
        <div className="banner-carousel">
          <div
            className="banner-track"
            style={{
              width: `${bannerList.length * 100}%`,
              transform: `translateX(-${currentBannerIndex * (100 / bannerList.length)}%)`,
              transition: 'transform 0.6s ease'
            }}
          >
            {bannerList.map((b) => (
              <div key={b._id} className="banner-slide" style={{ width: `${100 / bannerList.length}%` }}>
                <img src={b.url} alt={b.title || 'Banner'} />
              </div>
            ))}
          </div>
          {bannerList.length > 1 && (
            <>
              <button
                className="banner-prev-btn"
                onClick={() => { clearInterval(bannerAutoRef.current); goToBanner((currentBannerIndex - 1 + bannerList.length) % bannerList.length); }}
                aria-label="Previous banner"
              >‹</button>
              <button
                className="banner-next-btn"
                onClick={() => { clearInterval(bannerAutoRef.current); goToBanner((currentBannerIndex + 1) % bannerList.length); }}
                aria-label="Next banner"
              >›</button>
              <div className="banner-dots">
                {bannerList.map((_, i) => (
                  <span
                    key={i}
                    className={`banner-dot${i === currentBannerIndex ? ' active' : ''}`}
                    onClick={() => { clearInterval(bannerAutoRef.current); goToBanner(i); }}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* Hero Section */}
      <section className="hero-section">
        <NoticeBar />
        <div className="hero-background">
          <div className="hero-content">
            <h1 className="hero-title"><span style={{ color: 'orange' }}>Vidya Niketan</span>  Hapur Best Coaching Institute for IX to XII</h1>
            <h2 className="hero-subtitle">CBSE, JEE & NEET Coaching in Hapur with Expert Teachers</h2>
            <p className="hero-subtitle">
              Learn with love and grow with guidance. Access live classes, study materials, and expert guidance.
            </p>
            <div className="hero-buttons">
              <button className="btn btn-primary" onClick={() => navigate('/signup')}>
                Get Started Today
              </button>
              <button className="btn btn-secondary" onClick={() => navigate('/login')}>
                Already a Member? Login
              </button>
            </div>
            <div className="hero-stats">
              <div className="stat">
                <h3>India's Best</h3>
                <p>Faculties</p>
              </div>
              <div className="stat">
                <h3>Free</h3>
                <p>Library</p>
              </div>
              <div className="stat">
                <h3>Limited</h3>
                <p>Seats</p>
              </div>
            </div>
          </div>
          <div className="hero-image">
            <div className="hero-collage">
              <div className="collage-item collage-frame frame-1">
                <img src="/images/hero-3.jpg" alt="Collage 1" />
              </div>
              <div className="collage-item collage-frame frame-2">
                <img src="/images/hero-4.jpg" alt="Collage 2" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section className="results-section">
        <div className="container">
          <h2 className="section-title">Academic Excellence</h2>
          <p className="section-subtitle">Building Future Achievers</p>
          
          <div className="results-grid">
            <div className="result-card">
              <div className="result-badge">2026</div>
              <div className="result-exam">JEE Advanced Program</div>
              <div className="result-count">Admissions Open</div>
            </div>
            <div className="result-card">
              <div className="result-badge">Elite Batch</div>
              <div className="result-exam">NEET 2026</div>
              <div className="result-count">Limited 40 Students</div>
            </div>
            <div className="result-card">
              <div className="result-badge">Mentorship</div>
              <div className="result-exam">NDA CDS</div>
              <div className="result-count">Starting Soon</div>
            </div>
            <div className="result-card">
              <div className="result-badge">BOARDS</div>
              <div className="result-exam">Years of Proven Records</div>
              <div className="result-count">Be The Part Of Our Success</div>
            </div>
          </div>
        </div>
      </section>

      {/* AKSHAY Section */}
      <section className="learn-earn-section">
        <div className="container">
          <div className="akshay-split">
            {/* Left — image */}
            <div className="akshay-split__img-col">
              <div className="akshay-split__img-frame">
                <img src="/images/Akshay.png" alt="Akshay Store" className="akshay-split__img" />
                <div className="akshay-split__img-glow" />
              </div>
            </div>

            {/* Right — heading + promo */}
            <div className="akshay-promo">
              <div className="akshay-logo-wrap">
                <span className="akshay-logo">
                  {'AKSHAY'.split('').map((ch, i) => (
                    <span key={i} className={`akshay-letter akshay-letter-${i}`}>{ch}</span>
                  ))}
                </span>
                <div className="akshay-tagline">Premium Store by Vidya Niketan</div>
              </div>
              <div className="akshay-promo__pills">
                <span>✅ 100% Genuine</span>
                <span>⚡ Fast Delivery</span>
                <span>💰 Best Prices</span>
              </div>
              <div style={{marginTop: 28, display: 'flex', justifyContent: 'center'}}>
                <button className="learn-earn-store-btn" onClick={()=>navigate('/store')}>Click to view store</button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="container">
          <h2 className="section-title">Why Choose Vidya Niketan?</h2>
          <div className="features-grid">
            {features.map((feature, index) => (
              <div key={index} className="feature-card">
                <div className="feature-icon">{feature.icon}</div>
                <h3>{feature.title}</h3>
                <p>{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Section - Images */}
      <section className="gallery-section">
        <div className="container">
          <h2 className="section-title">Memories at Vidya Niketan</h2>
          <p className="gallery-desc">A glimpse of our events, achievements, and moments from Vidya Niketan Hapur.</p>
          {galleryImages[currentCarouselIndex]?.title && (
            <p className="gallery-carousel-title">{galleryImages[currentCarouselIndex].title}</p>
          )}
          {galleryImages.length > 0 ? (
            <div className="gallery-carousel-wrapper">
              <button
                className="gallery-carousel-arrow gallery-carousel-arrow--left"
                onClick={() => { clearInterval(autoScrollRef.current); scrollCarousel(-1); }}
                aria-label="Previous"
              >&#8249;</button>
              <div
                className="gallery-carousel-track"
                ref={carouselRef}
              >
                {galleryImages.map((img, idx) => (
                  <div className="gallery-carousel-card" key={img._id || idx}>
                    <img src={img.url} alt={img.title || 'Gallery'} />
                  </div>
                ))}
              </div>
              <button
                className="gallery-carousel-arrow gallery-carousel-arrow--right"
                onClick={() => { clearInterval(autoScrollRef.current); scrollCarousel(1); }}
                aria-label="Next"
              >&#8250;</button>
            </div>
          ) : (
            <p className="gallery-empty-msg">No images uploaded yet. Check back soon!</p>
          )}
        </div>
      </section>

      {/* Exam Preparation Section */}
      <section className="exams-section">
        <div className="container">
          <h2 className="section-title">Prepare for Multiple Exams</h2>
          <div className="exams-grid">
            {exams.map((exam, index) => (
              <div key={index} className="exam-tag">{exam}</div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials-section">
        <div className="container">
          <h2 className="section-title">Students ❤️ Vidya Niketan</h2>
          <p className="section-subtitle">Hear from our successful students</p>
          
          <div className="testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="testimonial-card">
                <div className="quote-icon">"</div>
                <p className="testimonial-text">{testimonial.text}</p>
                <div className="testimonial-author">
                  <div className="author-avatar">
                    {testimonial.name.charAt(0)}
                  </div>
                  <div className="author-info">
                    <h4>{testimonial.name}</h4>
                    <p>{testimonial.exam}</p>
                    <span className="achievement-badge">{testimonial.achievement}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* App Download Section */}
      <section className="app-section">
        <div className="container">
          <div className="app-content">
            <div className="app-info">
              <h2>Download Our App</h2>
              <p>Learn on the go! Access all features, take live classes, and track progress on your mobile.</p>
              <div className="app-buttons">
                <button className="app-btn">
                  <span className="icon">🍎</span> App Store
                </button>
                <button className="app-btn">
                  <span className="icon">🤖</span> Google Play
                </button>
              </div>
              <p className="app-stats">Join 15M+ students already learning on our app</p>
            </div>
            <div className="app-image">
              <div className="phone-mockup">📱</div>
            </div>
          </div>
        </div>
      </section>

      {/* YouTube Channels Section */}
      <section className="youtube-section">
        <div className="container">
          <h2 className="section-title">Join The Vidya Niketan Family</h2>
          <p className="section-subtitle">Explore our YouTube channels and subscribe for free content</p>
         <div className="social-links">
                                <a className="linkedin-social-link" href="https://www.linkedin.com/in/vidya-niketan-4515873b1/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn"><FaLinkedin size={20} color="white" /></a>
                                <a className="facebook-social-link" href="https://www.facebook.com/profile.php?id=61585277140671" target="_blank" rel="noopener noreferrer" aria-label="Facebook"><FaFacebookF size={20} color="white" /></a>
                                <a className="instagram-social-link" href="https://www.instagram.com/vidya_niketan25/" target="_blank" rel="noopener noreferrer" aria-label="Instagram"><FaInstagram size={20} color="white" /></a>
                                <a className="youtube-social-link" href="https://www.youtube.com/@vidyaniketanfoundationhapur" target="_blank" rel="noopener noreferrer" aria-label="YouTube"><FaYoutube size={20} color="white" /></a>
                                <a className="x-social-link" href="https://x.com/VIDYANIKET73341" target="_blank" rel="noopener noreferrer" aria-label="X"><FaXTwitter size={20} color="white" /></a>
              </div> 
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-content">
          <h2>Strong Foundations. Top Results. Bright Future.</h2>
          <p>Prepare for JEE, NEET & Board Exams with expert faculty from top-ranked schools.</p>
          <button className="btn btn-primary btn-large" onClick={() => navigate('/signup')}>
            Limited Seats – Join Today
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-column">
              <h4>Vidya Niketan</h4>
              <p>Best coaching institute in Hapur for CBSE Class IX-XII, JEE & NEET preparation.</p>
              
            </div>

            <div className="footer-column">
              <h4>Contact Info</h4>
              <ul>
                <li>📍 Vidya Niketan, Old NH 24, Babugarh Cantt, Hapur, Uttar Pradesh</li>
                <li>📞 <a href="tel:+916376333741">+91 6376333741</a></li>
              </ul>
            </div>

            <div className="footer-column">
              <h4>Quick Links</h4>
              <ul>
                <li><button className="footer-link-btn" onClick={() => window.location.href='/about-us'}>About Us</button></li>
                <li><button className="footer-link-btn" onClick={() => window.location.href='/contact-us'}>Contact Us</button></li>
                <li><button className="footer-link-btn" onClick={() => window.location.href='/our-courses'}>Our Courses</button></li>
                <li><button className="footer-link-btn" onClick={() => window.location.href='/results'}>Results</button></li>
              </ul>
            </div>

            <div className="footer-column">
              <h4>Support</h4>
              <ul>
                <li><button className="footer-link-btn" onClick={() => window.location.href='/privacy-policy'}>Privacy Policy</button></li>
                <li><button className="footer-link-btn" onClick={() => window.location.href='/terms-of-use'}>Terms of Use</button></li>
                <li><button className="footer-link-btn" onClick={() => window.location.href='/faq'}>FAQ</button></li>
                <li><button className="footer-link-btn" onClick={() => window.location.href='/grievance-policy'}>Grievance Policy</button></li>
              </ul>
            </div>
          </div>

          <div className="footer-bottom">
            <p>&copy; 2026 Vidya Niketan. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
